import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import { loadSquareJs } from "@/lib/square";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Guest-facing £100 refundable deposit page. Justina sends the link
 * (/deposit/:bookingId/:token) by email/text; the guest enters their own card
 * and the hold (pre-authorisation — no money moves) is placed with Square.
 */
const DepositPage = () => {
  const { bookingId, token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { delayed_until, card_last4 }
  const cardRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/deposit/${bookingId}/${token}`);
        if (disposed) return;
        setInfo(data);
        if (data.already_held) return;
        await loadSquareJs(data.square.env);
        if (disposed) return;
        const payments = window.Square.payments(data.square.application_id, data.square.location_id);
        const card = await payments.card();
        await card.attach("#sq-guest-card");
        if (disposed) {
          card.destroy?.();
          return;
        }
        cardRef.current = card;
        setCardReady(true);
      } catch (e) {
        if (!disposed) {
          const detail = e?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "This deposit link is invalid or has expired.");
        }
      }
    })();
    return () => {
      disposed = true;
      cardRef.current?.destroy?.();
      cardRef.current = null;
    };
  }, [bookingId, token]);

  const placeHold = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        throw new Error(result.errors?.[0]?.message || "Your card couldn't be verified — please check the details.");
      }
      const { data } = await axios.post(`${API}/deposit/${bookingId}/${token}`, { source_id: result.token });
      setDone(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : e.message || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (d) =>
    d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex flex-col" data-testid="deposit-page">
      <header className="bg-[#0F0E0E] py-6 px-6 text-center">
        <p className="font-serif text-[#F5EFE6] text-xl tracking-[0.28em] uppercase">Walnut Lakes</p>
        <p className="text-[#6A5F58] text-[10px] tracking-[0.22em] uppercase mt-1.5">Holiday Park · Lincolnshire</p>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden shadow-sm">
          {error && !info ? (
            <div className="p-8 text-center" data-testid="deposit-invalid">
              <AlertCircle size={28} className="mx-auto text-[#B76E79]" />
              <h1 className="font-serif text-2xl text-[#14100F] mt-4">This link isn't active</h1>
              <p className="text-sm text-[#6A5F58] mt-3 leading-relaxed">{error}</p>
              <p className="text-xs text-[#6A5F58] mt-3 leading-relaxed">
                If we've emailed you more than once, please use the <strong>newest</strong> email — or just call us and we'll sort it in a minute.
              </p>
              <p className="text-xs text-[#6A5F58] mt-4">
                Need help? Call Justina on <strong>07982 844201</strong> or email{" "}
                <a href="mailto:justina@walnutlakes.co.uk" className="text-[#B76E79] underline">justina@walnutlakes.co.uk</a>
              </p>
            </div>
          ) : !info ? (
            <div className="p-12 flex items-center justify-center gap-2 text-[#6A5F58] text-sm" data-testid="deposit-loading">
              <Loader2 size={16} className="animate-spin" /> Loading your deposit…
            </div>
          ) : done || info.already_held ? (
            <div className="p-8 text-center" data-testid="deposit-success">
              <CheckCircle2 size={32} className="mx-auto text-[#3B8C4E]" />
              <h1 className="font-serif text-2xl text-[#14100F] mt-4">All set, {info.guest_first_name}</h1>
              <p className="text-sm text-[#6A5F58] mt-3 leading-relaxed">
                {info.already_held && !done
                  ? "Your £100 deposit hold is already in place — nothing more to do."
                  : <>Your £100 deposit hold is in place{done?.card_last4 ? ` on your card ••${done.card_last4}` : ""}. Remember — <strong>no money has left your account</strong>; the hold simply drops off after your stay.</>}
              </p>
              <p className="text-xs text-[#6A5F58] mt-4">
                {info.lodge_name} · {fmtDate(info.start_date)} → {fmtDate(info.end_date)}
              </p>
              <Link to="/" className="inline-block mt-6 text-[10px] uppercase tracking-[0.18em] text-[#B76E79] hover:underline">
                Back to walnutlakes.co.uk
              </Link>
            </div>
          ) : (
            <div className="p-6 md:p-8">
              <div className="text-center">
                <ShieldCheck size={28} className="mx-auto text-[#B76E79]" />
                <h1 className="font-serif text-2xl text-[#14100F] mt-3">£100 refundable deposit</h1>
                <p className="text-sm text-[#6A5F58] mt-2">
                  {info.lodge_name} · {fmtDate(info.start_date)} → {fmtDate(info.end_date)}
                </p>
              </div>

              <div className="mt-5 bg-[#FBF7F1] border border-[#EDE3D2] rounded-xl p-4 text-[13px] text-[#3A322E] leading-relaxed">
                Hi {info.guest_first_name} — before your stay we ask for a <strong>£100 security deposit</strong>.
                This is a <strong>pre-authorisation only: no money leaves your account</strong>. The hold drops off
                automatically after your stay once the lodge is left as found.
              </div>

              {error && (
                <p className="mt-4 text-xs text-[#8C4E58] bg-[#8C4E58]/10 border border-[#8C4E58]/30 rounded-lg px-3 py-2" data-testid="deposit-error">
                  {error}
                </p>
              )}

              <div className="mt-5">
                <div id="sq-guest-card" className="min-h-[90px] bg-white rounded-lg border border-[#DDD1BE]" data-testid="deposit-card-form" />
                <button
                  type="button"
                  onClick={placeHold}
                  disabled={!cardReady || busy}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[11px] font-brand tracking-[0.18em] uppercase disabled:opacity-50"
                  data-testid="deposit-submit"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                  {busy ? "Placing your hold…" : "Place £100 hold"}
                </button>
                <p className="mt-3 text-[10px] text-[#6A5F58] text-center flex items-center justify-center gap-1.5">
                  <Lock size={10} /> Card details go straight to Square — we never see or store them.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="pb-8 text-center text-[10px] text-[#6A5F58]">
        Walnut Lakes Holiday Park · Main Road, Algarkirk, Boston PE20 2LQ
      </footer>
    </div>
  );
};

export default DepositPage;
